/**
 * Query-key factory. Centralizes the key shape so invalidations stay precise
 * (`queryClient.invalidateQueries({ queryKey: qk.users() })` clears all
 * variants under `['users', ...]`).
 */

import type { TranslationListFilters } from '@/schemas/admin/translations';
import type { NewsletterSubscribersFilters } from '@/schemas/admin/newsletter';
import type { ProductListFilters } from '@/schemas/admin/commerce/products';
import type { OrderListFilters } from '@/schemas/admin/commerce/orders';
import type { ReturnListFilters } from '@/schemas/admin/commerce/returns';
import type { ReviewListFilters } from '@/schemas/admin/commerce/reviews';
import type { WaitlistFilters } from '@/schemas/admin/phase2/waitlist';
import type { PillarMixWindow } from '@/schemas/admin/phase2/pillars';
import {
  DEFAULT_COPY_CATALOG_PAGE_SIZE,
  type CopyCatalogFilters,
  type CopyFlagFilter,
} from '@/schemas/admin/copy';

export const qk = {
  dashboard: () => ['dashboard'] as const,
  commerceDashboard: () => ['commerce', 'dashboard'] as const,
  audit: (filter?: { actorId?: string }) =>
    filter?.actorId ? (['audit', { actorId: filter.actorId }] as const) : (['audit'] as const),
  users: (q?: string) => ['users', { q: q ?? '' }] as const,
  user: (id: string) => ['users', id] as const,
  userReveal: (id: string) => ['users', id, 'reveal'] as const,
  moderationQueue: (state: string) => ['moderation', 'queue', state] as const,
  policies: () => ['policies'] as const,

  // Phase 2 — curation & content ops
  homepageSections: () => ['homepage', 'sections'] as const,
  homepagePreview: (locale: 'en' | 'ar') => ['homepage', 'preview', locale] as const,
  storyCollections: () => ['stories', 'collections'] as const,
  announcements: () => ['announcements'] as const,
  featureFlags: () => ['feature-flags'] as const,
  translations: (filters: TranslationListFilters) =>
    [
      'translations',
      {
        status: filters.status ?? '',
        subject_kind: filters.subject_kind ?? '',
        assigned_to: filters.assigned_to ?? '',
        limit: filters.limit ?? 25,
      },
    ] as const,
  newsletterIssues: () => ['newsletter', 'issues'] as const,
  newsletterIssue: (id: string) => ['newsletter', 'issues', id] as const,
  newsletterSubscribers: (filters: NewsletterSubscribersFilters) =>
    [
      'newsletter',
      'subscribers',
      {
        locale: filters.locale ?? '',
        cohort: filters.cohort ?? '',
        reveal: filters.reveal ?? false,
        limit: filters.limit ?? 25,
      },
    ] as const,

  // Phase 3 — commerce catalog & inventory
  designers: () => ['commerce', 'designers'] as const,
  designer: (id: string) => ['commerce', 'designers', id] as const,
  products: (filters: ProductListFilters) =>
    [
      'commerce',
      'products',
      {
        q: filters.q ?? '',
        designer_id: filters.designer_id ?? '',
        availability: filters.availability ?? '',
        include_deleted: filters.include_deleted ?? false,
        limit: filters.limit ?? 100,
      },
    ] as const,
  product: (id: string) => ['commerce', 'products', id] as const,
  inventory: (variantIds: readonly string[]) =>
    ['commerce', 'inventory', { variants: [...variantIds].sort().join(',') }] as const,
  discountCodes: () => ['commerce', 'discount-codes'] as const,

  // Phase 4 — commerce operations
  orders: (filters: OrderListFilters) =>
    [
      'commerce',
      'orders',
      {
        state: filters.state ?? '',
        designer: filters.designer ?? '',
        limit: filters.limit ?? 25,
      },
    ] as const,
  order: (id: string) => ['commerce', 'orders', id] as const,
  orderCustomerReveal: (id: string) => ['commerce', 'orders', id, 'customer-reveal'] as const,
  returns: (filters: ReturnListFilters) =>
    [
      'commerce',
      'returns',
      {
        state: filters.state ?? '',
        limit: filters.limit ?? 100,
      },
    ] as const,
  reviews: (filters: ReviewListFilters) =>
    [
      'commerce',
      'reviews',
      {
        state: filters.state ?? '',
        limit: filters.limit ?? 100,
      },
    ] as const,
  payouts: () => ['commerce', 'payouts'] as const,
  shippingZones: () => ['commerce', 'shipping-zones'] as const,
  shippingRates: () => ['commerce', 'shipping-rates'] as const,
  taxRules: () => ['commerce', 'tax-rules'] as const,

  // Phase 5 — Phase 2 editorial
  launchMode: () => ['phase2', 'launch-mode'] as const,
  waitlist: (filters: WaitlistFilters) =>
    ['phase2', 'waitlist', { state: filters.state ?? '' }] as const,
  waitlistMetrics: () => ['phase2', 'waitlist', 'metrics'] as const,
  pillarMix: (window: PillarMixWindow) => ['phase2', 'pillars', 'mix', window] as const,
  culturalMomentGaps: () => ['phase2', 'cultural-moments', 'gaps'] as const,
  haesPrinciples: () => ['phase2', 'haes', 'principles'] as const,
  staleBrands: () => ['phase2', 'brands', 'stale'] as const,
  promptSlaBreaches: () => ['phase2', 'prompts', 'sla-breaches'] as const,
  promptsList: () => ['phase2', 'prompts', 'list'] as const,

  // Site copy CMS — catalog, one key, pending drafts, publish history
  copyCatalog: (filters: CopyCatalogFilters) =>
    [
      'copy',
      'catalog',
      {
        q: filters.q ?? '',
        namespace: filters.namespace ?? '',
        surface: filters.surface ?? '',
        status: filters.status ?? '',
        flag: filters.flag ?? '',
        limit: filters.limit ?? DEFAULT_COPY_CATALOG_PAGE_SIZE,
      },
    ] as const,
  copyFlagCount: (flag: CopyFlagFilter) => ['copy', 'catalog', 'flag-count', flag] as const,
  copyKey: (key: string) => ['copy', 'keys', key] as const,
  copyPending: () => ['copy', 'pending'] as const,
  copyBundles: () => ['copy', 'bundles'] as const,
  copyBundle: (version: number) => ['copy', 'bundles', version] as const,

  // Community — chapters, groups, events
  chapters: () => ['community', 'chapters'] as const,
  groups: () => ['community', 'groups'] as const,
  events: () => ['community', 'events'] as const,
};
